import { isIOS } from '@futuweb/tool-browser';
import NN from '@futuweb/tool-nn';
import copy from 'copy-to-clipboard';
import { ref } from 'vue-demi';
import {
    CHANNEL_APP_SCHEME,
    fetchDownloadImage,
    fetchGetEnableChannel,
    fetchShowDiverseShare,
    fetchSystemShare
} from '../common/app';
import { BASE_APP_VERSION, IOS_FACEBOOK_IMAGE_DIRECT_VERSION } from './constants';
import { _lang } from './lang';
import log from './log';
import { Channel, ShareMethod } from './type';
import { isApp, toast } from './utils';

export { isApp };
// 是否低版本
export const isLowVersion = () => {
    if (NN.laterThanAndroid(BASE_APP_VERSION)) {
        return false;
    }
    if (NN.laterThanIOS(BASE_APP_VERSION)) {
        return false;
    }
    return true;
};

export function getShareText(params) {
    const { desc, code, inviteLink, channel, appDataList } = params;
    if (window?.FEGOEditor) {
        return desc;
    }
    let appCode = code;
    let appInviteLink = inviteLink;
    if (channel && appDataList?.length) {
        // 获取渠道对应的code和inviteLink
        const appData = appDataList.find((item) => item.channel === channel);
        appCode = appData?.code || code;
        appInviteLink = appData?.inviteLink || inviteLink;
    }
    return desc
        .replace(/\{code\}/g, appCode)
        .replace(/\{inviteLink\}/g, appInviteLink)
        .replace(/\{appCode\}/g, appCode)
        .replace(/\{appInviteLink\}/g, appInviteLink);
}

/**
 * 异常处理
 */
function _handleError(error, options = {}) {
    const { silent = false } = options;
    log.error('异常处理', {
        error
    });
    if (!silent) {
        toast.open(_lang('requestFail'));
    }
    return;
}

/**
 * 下载图片
 */
export async function downloadImage(images) {
    try {
        await fetchDownloadImage({ images });
        return true;
    } catch (error) {
        log.error('下载图片异常', {
            images,
            error
        });
        _handleError(error, {
            // 下载图片异常会有系统弹窗 所以不弹toast
            silent: true
        });
        return false;
    }
}

/**
 * 复制文案
 */
export function codeText(text, isToast = true) {
    copy(text);
    if (isToast) {
        toast.open(_lang('copySuccess'));
    }
}

// 是否展示渠道不可用弹窗
export const showDisablePopup = ref(false);

export const enableChannels = ref([]);
/**
 * 获取可使用的渠道
 */
export async function getEnableChannel() {
    const channels = Object.values(Channel).filter((item) => item !== Channel.SYSTEM);
    try {
        const res = await fetchGetEnableChannel({ channels });
        enableChannels.value = res;
    } catch (error) {
        log.error('获取可用渠道异常', {
            channels,
            error
        });
        _handleError(error, {
            // 静默处理
            silent: true
        });
    }
}

/**
 * 能否渠道分享
 */
export function canShare(channel) {
    const specialAccess = isIOS() && channel === Channel.XIAOHONGSHU;
    const baseAccess = channel === Channel.SYSTEM;
    return enableChannels.value.includes(channel) || specialAccess || baseAccess;
}

/**
 * 分享
 */
export async function share({ channel, image, desc, code, inviteLink, appDataList, showShareDesc = true }) {
    const text = getShareText({
        desc,
        inviteLink,
        code,
        channel,
        appDataList
    });
    // 复制文案
    // 当 showShareDesc 为 false（没有展示文案）时，不显示toast
    codeText(text, showShareDesc);
    const shareMethod = getShareMethod({ channel });

    if (shareMethod === ShareMethod.TO_HOME) {
        // IOS下单图分享表现不佳，所以下载图片并跳转到app
        const res = await downloadImage([image]);
        if (!res) {
            return;
        }
        const target = CHANNEL_APP_SCHEME[channel];
        log.info('跳首页分享', {
            channel,
            target
        });

        // 延后执行
        setTimeout(() => {
            // 下载成功则跳转
            location.href = target;
        }, 0);
    } else {
        let targetChannel = channel;
        if (shareMethod === ShareMethod.SYSTEM_PANEL) {
            targetChannel = Channel.SYSTEM;
        }

        log.info('渠道分享', {
            shareMethod,
            channel,
            image,
            targetChannel
        });

        return channelShare({
            channel: targetChannel,
            image,
            text
        });
    }
}

/**
 * 渠道分享
 */
async function channelShare(params) {
    const { channel, image, text } = params;
    try {
        await fetchShowDiverseShare({
            channel,
            image,
            text
        });
    } catch (error) {
        log.error('公域分享异常', {
            error,
            channel,
            image
        });
        _handleError(error);
    }
}

/**
 * 系统分享
 */
export async function systemShare(inviteLink) {
    try {
        await fetchSystemShare(inviteLink);
    } catch (error) {
        log.error('系统分享异常', {
            error,
            inviteLink
        });
        _handleError(error, { silent: true });
    }
}

/**
 * 获取分享方式
 */
export function getShareMethod({ channel }) {
    // IOS
    if (isIOS()) {
        if ([Channel.FACEBOOK].includes(channel)) {
            // 旧版本兼容
            if (NN.beforeThanIOS(IOS_FACEBOOK_IMAGE_DIRECT_VERSION)) {
                return ShareMethod.SYSTEM_PANEL;
            }
            return ShareMethod.IMAGE_DIRECT;
        }
        if ([Channel.REDDIT].includes(channel)) {
            return ShareMethod.TO_HOME;
        }
        return ShareMethod.SYSTEM_PANEL;
    }
    // 安卓
    return ShareMethod.IMAGE_DIRECT;
}
